import { useRef, useState } from 'react';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  Box,
  Button,
  Typography,
  Chip,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  DataTable,
  PageHeader,
  MetricCard,
  SectionCard,
  LoadingState,
  Column,
} from '@/components/shared';
import useBreakpoint from '@/hooks/useBreakpoint';
import { SpreadPrompt } from '@/hooks/usePayrollProfileLinking';
import financeClientsService from '@/services/financeClients';
import { BulkReport, BulkReportItem, CandidateTransaction } from '@/services/financePayroll';

type AvailableProfile = { id: string; firstName: string; lastName: string; email: string; developerType: string | null };

type Props = {
  report: BulkReport | null;
  items: BulkReportItem[];
  isLoading: boolean;
  candidates: CandidateTransaction[];
  loadingCandidates: boolean;
  onLoadCandidates: () => void;
  onLinkTransaction: (transactionId: string) => Promise<void> | void;
  onUnlinkTransaction: (transactionId: string) => Promise<void> | void;
  onLinkProfile: (
    itemId: string,
    profileId: string | null,
    profileName?: string
  ) => Promise<void> | void;
  spread: SpreadPrompt | null;
  onApplySpread: (itemIds: string[]) => Promise<void> | void;
  onDismissSpread: () => void;
  isApplyingSpread: boolean;
  onDelete: () => Promise<void> | void;
};

/**
 * Badges for links the system made on its own. 'manual' and legacy null links
 * get no badge — an unbadged green check means a human put it there.
 */
const LINK_SOURCE_BADGE: Record<string, { label: string; color: string; hint: string }> = {
  remembered: {
    label: 'Remembered',
    color: '#054AD9',
    hint: 'Reused from a profile you linked by hand before',
  },
  roster: {
    label: 'Warp roster',
    color: '#16a34a',
    hint: 'Matched via the Warp worker roster, which is verified by email',
  },
  auto: {
    label: 'Name guess',
    color: '#f59e0b',
    hint: 'Matched by name only — worth checking',
  },
};

const fmt = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const fmtDate = (d: string): string => {
  if (!d) return '—';
  const date = new Date(`${d}T00:00:00`);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const FinanceXPayrollReportComponent = ({
  report,
  items,
  isLoading,
  candidates,
  loadingCandidates,
  onLoadCandidates,
  onLinkTransaction,
  onUnlinkTransaction,
  onLinkProfile,
  spread,
  onApplySpread,
  onDismissSpread,
  isApplyingSpread,
  onDelete,
}: Props): JSX.Element => {
  const { isMobile } = useBreakpoint();
  const [linkingItemId, setLinkingItemId] = useState<string | null>(null);
  const [linkSearchResults, setLinkSearchResults] = useState<AvailableProfile[]>([]);
  const [linkSearchLoading, setLinkSearchLoading] = useState(false);
  const [linkQuery, setLinkQuery] = useState('');
  const [staffOnly, setStaffOnly] = useState(true);
  const searchSeq = useRef(0);

  /** Clear per-row search state; staffOnly persists as a session preference. */
  const closeLinkDialog = (): void => {
    setLinkingItemId(null);
    setLinkSearchResults([]);
    setLinkQuery('');
  };

  const runProfileSearch = async (value: string, onlyStaff: boolean): Promise<void> => {
    if (value.length < 2) {
      setLinkSearchResults([]);
      return;
    }
    // Guard against out-of-order responses: only the latest query wins.
    const seq = searchSeq.current + 1;
    searchSeq.current = seq;
    setLinkSearchLoading(true);
    try {
      const results = await financeClientsService.searchProfiles(value, { staffOnly: onlyStaff });
      if (seq === searchSeq.current) setLinkSearchResults(results);
    } finally {
      if (seq === searchSeq.current) setLinkSearchLoading(false);
    }
  };
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showTxLinker, setShowTxLinker] = useState(false);
  const [linkingTxId, setLinkingTxId] = useState<string | null>(null);
  const [unlinkingTxId, setUnlinkingTxId] = useState<string | null>(null);

  if (isLoading || !report) return <LoadingState message="Loading report..." />;

  const matchedCount = items.filter((i) => i.profileId).length;
  const unmatchedCount = items.length - matchedCount;
  const linked = report.linkedTransactions ?? [];
  const linkedTotal = report.linkedTotal ?? 0;
  const difference = report.totalNet - linkedTotal;
  const isFullyLinked = linked.length > 0 && Math.abs(difference) < 1;

  const columns: Column<BulkReportItem>[] = [
    {
      id: 'lastName',
      label: 'Name',
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {r.profileId ? (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 16, color: LINK_SOURCE_BADGE[r.linkSource ?? '']?.color ?? '#16a34a' }}
            />
          ) : (
            <ErrorOutlineIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
          )}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" fontWeight={500}>
                {r.firstName} {r.lastName}
              </Typography>
              {/* Automatically-applied links stay visibly distinct from a name
                  guess, so what the matcher decided can be spot-checked. */}
              {LINK_SOURCE_BADGE[r.linkSource ?? ''] && (
                <Chip
                  label={LINK_SOURCE_BADGE[r.linkSource ?? ''].label}
                  size="small"
                  title={LINK_SOURCE_BADGE[r.linkSource ?? ''].hint}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 500,
                    color: LINK_SOURCE_BADGE[r.linkSource ?? ''].color,
                    borderColor: LINK_SOURCE_BADGE[r.linkSource ?? ''].color,
                  }}
                  variant="outlined"
                />
              )}
            </Box>
            {r.profileName && r.profileName !== `${r.firstName} ${r.lastName}` && (
              <Typography variant="caption" color="textSecondary">
                Matched: {r.profileName}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: 'grossAmount',
      label: 'Gross',
      align: 'right' as const,
      render: (r) => <Typography variant="body2" fontWeight={500}>{fmt(r.grossAmount)}</Typography>,
    },
    {
      id: 'netAmount',
      label: 'Net Pay',
      align: 'right' as const,
      render: (r) => <Typography variant="body2">{fmt(r.netAmount)}</Typography>,
    },
    {
      id: 'reimbursements',
      label: 'Reimb.',
      align: 'right' as const,
      render: (r) => (
        <Typography variant="body2" color={r.reimbursements > 0 ? '#054AD9' : 'inherit'}>
          {r.reimbursements > 0 ? fmt(r.reimbursements) : '—'}
        </Typography>
      ),
    },
    {
      id: 'fees',
      label: 'Fees',
      align: 'right' as const,
      render: (r) => (
        <Typography variant="body2" color={r.fees > 0 ? '#dc2626' : 'inherit'}>
          {r.fees > 0 ? fmt(r.fees) : '—'}
        </Typography>
      ),
    },
    {
      id: 'country',
      label: 'Country',
      align: 'center' as const,
      render: (r) => (r.country ? <Chip label={r.country} size="small" /> : <span>—</span>),
    },
    {
      id: 'profileId',
      label: 'Match',
      align: 'center' as const,
      render: (r) => (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setLinkingItemId(r.id); }}
          sx={{ color: r.profileId ? '#16a34a' : '#f59e0b' }}
        >
          <LinkIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const linkingItem = items.find((i) => i.id === linkingItemId) ?? null;

  let noOptionsText = 'No profiles match.';
  if (linkQuery.length < 2) noOptionsText = 'Type at least 2 characters to search...';
  else if (staffOnly) noOptionsText = 'No active contractors match. Try "Search all profiles".';

  return (
    <>
      <PageHeader
        title={`${report.vendorName} — ${fmtDate(report.periodStart)} to ${fmtDate(report.periodEnd)}`}
        actions={
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setConfirmDelete(true)}
            sx={{ borderRadius: '999px', textTransform: 'none' }}
          >
            Delete Report
          </Button>
        }
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <MetricCard label="Gross Total" value={fmt(report.totalGross)} />
        <MetricCard label="Net Pay" value={fmt(report.totalNet)} />
        <MetricCard label="Fees" value={fmt(report.totalFees)} color="#dc2626" />
        <MetricCard label="Reimbursements" value={fmt(report.totalReimbursements)} color="#054AD9" />
      </Box>

      {/* Bank Transaction Links */}
      <SectionCard
        title="Bank Transactions"
        action={
          <Button
            size="small"
            startIcon={<AddLinkIcon />}
            onClick={() => { setShowTxLinker(true); onLoadCandidates(); }}
            sx={{ borderRadius: '999px', textTransform: 'none' }}
          >
            Link Transactions
          </Button>
        }
      >
        {linked.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <AccountBalanceOutlinedIcon sx={{ fontSize: 32, color: '#71717A', mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              No bank transactions linked yet. Click &quot;Link Transactions&quot; to match this report to bank withdrawals.
            </Typography>
          </Box>
        ) : (
          <Box>
            {linked.map((tx) => (
              <Box
                key={tx.transactionId}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  borderBottom: '1px solid hsla(0,0%,0%,0.08)',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>{fmtDate(tx.date)}</Typography>
                  <Typography variant="caption" color="textSecondary">{tx.description}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500}>{fmt(tx.amount)}</Typography>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={unlinkingTxId === tx.transactionId}
                    onClick={async () => {
                      setUnlinkingTxId(tx.transactionId);
                      await onUnlinkTransaction(tx.transactionId);
                      setUnlinkingTxId(null);
                    }}
                  >
                    {unlinkingTxId === tx.transactionId
                      ? <CircularProgress size={16} />
                      : <LinkOffIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, background: '#F4F4F5', borderRadius: '0 0 12px 12px' }}>
              <Typography variant="body2" fontWeight={500}>
                Bank Total: {fmt(linkedTotal)}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                color={isFullyLinked ? '#16a34a' : '#f59e0b'}
              >
                {isFullyLinked
                  ? 'Fully reconciled'
                  : `Difference: ${fmt(difference)}`}
              </Typography>
            </Box>
          </Box>
        )}
      </SectionCard>

      <Box sx={{ mt: 3 }} />

      {/* Status chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Chip
          icon={<CheckCircleOutlineIcon />}
          label={`${matchedCount} matched`}
          size="small"
          color="success"
          variant="outlined"
        />
        {unmatchedCount > 0 && (
          <Chip
            icon={<ErrorOutlineIcon />}
            label={`${unmatchedCount} unmatched`}
            size="small"
            color="warning"
            variant="outlined"
          />
        )}
        {report.sourceFileName && (
          <Chip label={report.sourceFileName} size="small" variant="outlined" />
        )}
      </Box>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        rowKey={(r) => r.id}
        searchPlaceholder="Search people..."
        searchFields={(r) => `${r.firstName} ${r.lastName} ${r.profileName ?? ''}`}
        emptyMessage="No items in this report."
      />

      {/* Link Profile Dialog */}
      <Dialog
        open={!!linkingItemId}
        onClose={closeLinkDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={isMobile ? { sx: { borderRadius: '16px 16px 0 0', mt: 'auto', maxHeight: '90vh' } } : {}}
      >
        <DialogTitle>
          Link to Contractor Profile
          {linkingItem && (
            <Typography variant="body2" color="textSecondary">
              {linkingItem.firstName} {linkingItem.lastName} — {fmt(linkingItem.grossAmount)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Autocomplete
            options={linkSearchResults}
            getOptionLabel={(o) => `${o.firstName} ${o.lastName} (${o.email})`}
            value={linkSearchResults.find((p) => p.id === linkingItem?.profileId) ?? null}
            onChange={(_, val) => {
              if (linkingItemId) {
                onLinkProfile(
                  linkingItemId,
                  val?.id ?? null,
                  val ? `${val.firstName} ${val.lastName}` : undefined
                );
                closeLinkDialog();
              }
            }}
            onInputChange={(_, value) => {
              setLinkQuery(value);
              runProfileSearch(value, staffOnly);
            }}
            loading={linkSearchLoading}
            noOptionsText={noOptionsText}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField {...params} label="Search contractor by name..." size="small" autoFocus />
            )}
            fullWidth
          />
          {/* Default to real staff: `profiles` is mostly a recruiting database,
              so an unfiltered search buries the ~50 paid contractors. */}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                size="small"
                checked={!staffOnly}
                onChange={(e) => {
                  const next = !e.target.checked;
                  setStaffOnly(next);
                  runProfileSearch(linkQuery, next);
                }}
              />
            }
            label={
              <Typography variant="caption" color="textSecondary">
                Search all profiles (includes candidates)
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeLinkDialog}>Cancel</Button>
          {linkingItem?.profileId && (
            <Button
              color="error"
              onClick={() => {
                if (linkingItemId) {
                  onLinkProfile(linkingItemId, null);
                  closeLinkDialog();
                }
              }}
            >
              Unlink
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* "Link the other reports too?" — shown after a manual link when the same
          name sits unlinked on other reports. */}
      <Dialog
        open={!!spread}
        onClose={onDismissSpread}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={
          isMobile ? { sx: { borderRadius: '16px 16px 0 0', mt: 'auto', maxHeight: '90vh' } } : {}
        }
      >
        <DialogTitle>
          Link {spread?.profileName} everywhere?
          <Typography variant="body2" color="textSecondary">
            {spread?.matches.length === 1
              ? '1 more row with this name is still unlinked.'
              : `${spread?.matches.length} more rows with this name are still unlinked.`}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {(spread?.matches ?? []).map((m) => (
            <Box
              key={m.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                py: 1.25,
                borderBottom: '1px solid hsla(0,0%,0%,0.08)',
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {m.firstName} {m.lastName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {fmtDate(m.periodStart)} · {m.reportShortId}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={500}>
                {fmt(m.grossUsd)}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onDismissSpread} disabled={isApplyingSpread}>
            Not now
          </Button>
          <Button
            variant="contained"
            disabled={isApplyingSpread}
            onClick={() => onApplySpread((spread?.matches ?? []).map((m) => m.id))}
          >
            {isApplyingSpread ? (
              <CircularProgress size={16} />
            ) : (
              `Link all ${spread?.matches.length ?? 0}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Bank Transactions Dialog */}
      <Dialog
        open={showTxLinker}
        onClose={() => setShowTxLinker(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={isMobile ? { sx: { borderRadius: '16px 16px 0 0', mt: 'auto', maxHeight: '90vh' } } : {}}
      >
        <DialogTitle>
          Link Bank Transactions
          <Typography variant="body2" color="textSecondary">
            Select bank transactions that correspond to this payroll report ({fmt(report.totalNet)} net)
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* eslint-disable-next-line no-nested-ternary */}
          {loadingCandidates ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : candidates.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
              No unlinked transactions found for this processor in the date range.
            </Typography>
          ) : (
            candidates.map((tx) => (
              <Box
                key={tx.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  borderBottom: '1px solid hsla(0,0%,0%,0.08)',
                  '&:hover': { background: '#f8f9ff' },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {fmtDate(tx.date)} — {tx.vendorName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">{tx.description}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500}>{fmt(tx.amount)}</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={linkingTxId === tx.id}
                    onClick={async () => {
                      setLinkingTxId(tx.id);
                      await onLinkTransaction(tx.id);
                      setLinkingTxId(null);
                    }}
                    sx={{ borderRadius: '999px', textTransform: 'none', minWidth: 'auto' }}
                  >
                    {linkingTxId === tx.id ? <CircularProgress size={16} /> : 'Link'}
                  </Button>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowTxLinker(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete Report?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete this report and all {items.length} line items. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={isDeleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={isDeleting}
            onClick={async () => {
              setIsDeleting(true);
              await onDelete();
              setIsDeleting(false);
              setConfirmDelete(false);
            }}
            sx={{ borderRadius: '999px' }}
          >
            {isDeleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FinanceXPayrollReportComponent;
